import type { FieldPreset } from '@components/RingilDataTable/v3/columns/columnModel';

/**
 * Keys, sidebar groups and presets of the reservation list's v3 field catalogue — plain data, kept
 * apart from the renderers in reservationFields.tsx so their consistency is unit-tested.
 */

/** Persisted in tabs, so these strings never change; a field that goes away is dropped on load. */
export const ReservationField = {
  ApplicationId: 'APPLICATION_ID',
  State: 'STATE',
  OnBehalfOf: 'ON_BEHALF_OF',
  Location: 'LOCATION',
  Labels: 'LABELS',
  Ramp: 'RAMP',
  TimeslotDate: 'TIMESLOT_DATE',
  TimeslotTime: 'TIMESLOT_TIME',
  TimeslotTimezone: 'TIMESLOT_TIMEZONE',
  Customer: 'CUSTOMER',
  Carrier: 'CARRIER',
  Driver: 'DRIVER',
  ReservationName: 'RESERVATION_NAME',
  OrderNames: 'ORDER_NAMES',
  PreferredDate: 'PREFERRED_DATE',
  InfoExpandAll: 'INFO_EXPAND_ALL',
  Flags: 'FLAGS',
  Orders: 'ORDERS',
  Attachments: 'ATTACHMENTS',
} as const;

const F = ReservationField;

/** Sidebar sections — the cells the fields come from. */
export const ReservationFieldGroup = {
  Reservation: 'reservations.columnReservation',
  RampAndTime: 'reservations.columnRampAndTime',
  CustomerAndCarrier: 'reservations.columnCustomerAndCarrier',
  Reference: 'reservations.columnReference',
  AdditionalInfo: 'reservations.columnAdditionalInfo',
} as const;

/** Fallback texts of the group keys, for the sidebar section headings. */
export const RESERVATION_FIELD_GROUPS: { key: string; fallback: string }[] = [
  { key: ReservationFieldGroup.Reservation, fallback: 'ID' },
  { key: ReservationFieldGroup.RampAndTime, fallback: 'Ramp and time' },
  { key: ReservationFieldGroup.CustomerAndCarrier, fallback: 'Customer and carrier' },
  { key: ReservationFieldGroup.Reference, fallback: 'Reference' },
  { key: ReservationFieldGroup.AdditionalInfo, fallback: 'Additional information' },
];

/** Today's five cells as stacks of fields. */
export const RESERVATION_PRESETS: FieldPreset[] = [
  {
    key: 'RESERVATION',
    width: 200,
    header: ReservationFieldGroup.Reservation,
    headerDefault: 'ID',
    group: ReservationFieldGroup.Reservation,
    members: [F.ApplicationId, F.State, F.OnBehalfOf, F.Location, F.Labels],
  },
  {
    key: 'RAMP_AND_TIME',
    width: 180,
    header: ReservationFieldGroup.RampAndTime,
    headerDefault: 'Ramp and time',
    group: ReservationFieldGroup.RampAndTime,
    members: [F.Ramp, F.TimeslotDate, F.TimeslotTime, F.TimeslotTimezone],
  },
  {
    key: 'CUSTOMER_AND_CARRIER',
    width: 200,
    header: ReservationFieldGroup.CustomerAndCarrier,
    headerDefault: 'Customer and carrier',
    group: ReservationFieldGroup.CustomerAndCarrier,
    members: [F.Customer, F.Carrier, F.Driver],
  },
  {
    key: 'REFERENCE',
    width: 180,
    header: ReservationFieldGroup.Reference,
    headerDefault: 'Reference',
    group: ReservationFieldGroup.Reference,
    members: [F.ReservationName, F.OrderNames, F.PreferredDate],
  },
  {
    key: 'ADDITIONAL_INFO',
    width: 260,
    header: ReservationFieldGroup.AdditionalInfo,
    headerDefault: 'Additional information',
    group: ReservationFieldGroup.AdditionalInfo,
    members: [F.InfoExpandAll, F.Flags, F.Orders, F.Attachments],
  },
];

/** The old table's column ids (also what the company column order is made of) → the preset rebuilding that cell. */
const PRESET_OF_LEGACY_COLUMN: Record<string, string> = {
  applicationId: 'RESERVATION',
  timeslotAndRamp: 'RAMP_AND_TIME',
  customerAndCarrier: 'CUSTOMER_AND_CARRIER',
  reference: 'REFERENCE',
  manipulationUnits: 'ADDITIONAL_INFO',
};

/**
 * The columns of a new tab (the first "All", a new one, "Reset to default"): today's five cells, in
 * the order the company set for its reservation list. Cells the company order doesn't name follow in
 * their usual order, so a partial or outdated setting never loses one.
 */
export const reservationDefaultColumns = (companyColumnOrder: string[]): string[][] => {
  const named = companyColumnOrder.map(id => PRESET_OF_LEGACY_COLUMN[id]).filter((key, i, all) => key && all.indexOf(key) === i);
  const order = [...named, ...RESERVATION_PRESETS.map(preset => preset.key).filter(key => !named.includes(key))];
  return order.map(key => RESERVATION_PRESETS.find(preset => preset.key === key).members);
};

/**
 * Whether the additional-information tags of a column show as expandable tags: only beside "Expand
 * all", which opens them all. Anywhere else a field shows its plain value.
 */
export const reservationInfoTagsCollapsible = (columnMembers: string[]): boolean => columnMembers.includes(ReservationField.InfoExpandAll);
